import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { heroCopy, heroMediaV12 } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: EASE, delay },
});

// V1.3 Refinement 08 — full-bleed hero; the transparent header floats over it.
// A soft top scrim guarantees header legibility regardless of the photo.
export const HeroV13 = ({ image = heroMediaV12 }) => (
  <section
    id="hero"
    data-testid="hero-section"
    className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[var(--strollee-dark)]"
  >
    <motion.img
      src={image.url}
      alt={image.alt}
      data-testid="hero-media"
      initial={{ opacity: 0, scale: 1.06 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.4, ease: [0.25, 1, 0.5, 1] }}
      className="absolute inset-0 h-full w-full object-cover object-center"
    />
    {/* top scrim for the floating header */}
    <div className="pointer-events-none absolute inset-x-0 top-0 h-[180px] bg-gradient-to-b from-[#0F1A15]/40 to-transparent" />
    {/* bottom scrim for copy */}
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F1A15]/70 via-[#0F1A15]/15 to-transparent" />

    <div className="absolute inset-x-0 bottom-0 px-10 pb-12 lg:px-16 lg:pb-16">
      <div className="max-w-[600px]">
        <motion.div {...fadeUp(0.35)}>
          <span
            data-testid="hero-eyebrow-badge"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-white backdrop-blur-md"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#8FB89E]" />
            {heroCopy.eyebrow}
          </span>
        </motion.div>

        <h1
          data-testid="hero-main-headline"
          className="mt-6 text-[56px] font-semibold leading-[1.1] tracking-[-0.03em] text-white lg:text-[72px]"
        >
          {heroCopy.headlineLines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.45 + i * 0.12 }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          {...fadeUp(0.95)}
          className="mt-5 max-w-[480px] text-lg leading-relaxed text-white/80"
        >
          {heroCopy.support}
        </motion.p>

        <motion.div {...fadeUp(1.1)} className="mt-8 flex items-center gap-4">
          <a
            href="#build"
            data-testid="hero-primary-cta"
            className="group inline-flex items-center gap-2.5 rounded-full bg-[var(--strollee-accent)] px-7 py-3.5 text-sm font-semibold tracking-wide text-white transition-all duration-200 hover:-translate-y-[2px] hover:bg-[var(--strollee-accent-deep)] hover:shadow-[0_8px_28px_rgba(37,70,51,0.45)]"
          >
            {heroCopy.primaryCta}
            <ArrowRight
              size={16}
              strokeWidth={2.2}
              className="transition-transform duration-200 group-hover:translate-x-1"
            />
          </a>
          <a
            href="#s1"
            data-testid="hero-secondary-cta"
            className="group inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-7 py-3.5 text-sm font-semibold tracking-wide text-white backdrop-blur-md transition-all duration-200 hover:-translate-y-[2px] hover:bg-white hover:text-[var(--strollee-ink)]"
          >
            {heroCopy.secondaryCta}
            <ArrowUpRight
              size={16}
              strokeWidth={2.2}
              className="transition-transform duration-200 group-hover:translate-x-[2px] group-hover:-translate-y-[2px]"
            />
          </a>
        </motion.div>

        <motion.div
          {...fadeUp(1.25)}
          className="mt-10 flex items-center gap-6 text-[13px] font-medium text-white/70"
        >
          {heroCopy.proof.map((item, i) => (
            <span
              key={item}
              data-testid={`hero-proof-badge-${i}`}
              className="flex items-center gap-6"
            >
              {i > 0 && <span className="h-1 w-1 rounded-full bg-white/40" />}
              {item}
            </span>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);
